// © Microsoft Corporation. All rights reserved.

import { DesignSystem, accentFillHover, accentFillRest, accentForegroundCut, isDarkMode, neutralFillActive, neutralFillStealthActive, neutralFillStealthHover, neutralForegroundRest, neutralLayerCard } from "@microsoft/fast-components-styles-msft";
import { avatarPadding, avatarSize, reset } from "../utils/commonJssConstants";

import { applyLocalizedProperty } from "@microsoft/fast-jss-utilities";
import { enums } from "../utils/enums";

export const mainPadding = 15;
const overlayZIndex = "99999"; // for comments for overaly type and floating buttons

export const CommentStyles = (config: DesignSystem) =>  ({
    commentContainer: {
        ...reset,
        "max-width": "1024px",
        "min-width": "260px",
        margin: "32px auto",
        padding: `${mainPadding}px`,
        "box-sizing": "border-box",
        border: "1px solid #F2F2F2",
        "border-color": neutralFillActive,
        "border-radius": "8px",
        position: "relative",
        "scroll-margin-top": "9rem",

        "&, &:focus": {
            outline: "none"
        },

        "&:focus": {
            outline: "none"
        },

        /* CONTENT DISABLED MESSAGE */
        "& .cwt-content-disabled-container": {
            display: "flex",
            "align-items": "center",
            "justify-content": "stretch",

            "& .cwt-avatar-container": {
                position: "relative",
                display: "flex",
                margin: applyLocalizedProperty("0 13px 0 0", "0 0 0 13px", config.direction),
                padding: "3px 0",

                "& .cwt-commentbox-avatar": {
                    "min-width": avatarSize,
                    "max-width": avatarSize,
                    height: avatarSize,
                    "border-radius": "50%"
                }
            },

            "& .cwt-col-stretch": {
                width: "100%"
            }
        },

        "& .compact-floating-button": {
            position: "fixed",
            "z-index": overlayZIndex,
            [applyLocalizedProperty("left", "right", config.direction)]: "40px",
            bottom: "45px"
        },

        // 'ROOT PAGINATION'
        "& .cwt-show-more-comments-btn": {
            margin: "-4px 0 0px",
            "font-weight": "600",
            color: accentFillRest,
            fill: "currentColor",
            opacity: "1",

            "& .cwt-icon-label": {
                color: isDarkMode(config) ? "#5CB6FF" : accentFillRest
            }
        },

        "& .cwt-show-comments-btn": {
            display: "inline-flex",
            padding: "4px",
            margin: "-4px",
            "font-size": "13px",
            "font-weight": "500",
            color: isDarkMode(config) ? "#5CB6FF" : accentFillRest,
            fill: "currentColor",
            opacity: "1",
            "border-radius": "4px",

            "&:hover": {
                background: neutralFillStealthHover
            },

            "&.previous": {
                "margin-top": "20px"
            },
            "&.more": {
                "margin-bottom": "20px"
            },

            "& .cwt-icon-label": {
                color: isDarkMode(config) ? "#5CB6FF" : accentFillRest
            }
        },

        /* 'JOIN-THE-CONVERSATION BUTTON '*/
        "& .cwt-join-conversation-container": {
            display: "flex",
            "justify-content": "center",

            "& .cwt-link": {
                width: "80vw",
                "max-width": "480px",
                padding: "12px",
                "text-align": "center",
                "font-size": "16px",
                "font-weight": "600",
                background: neutralLayerCard,
                color: isDarkMode(config) ? "#5CB6FF" :accentFillRest,
                border: "1px solid ",
                "border-color": accentFillRest,
                "border-radius": "2px",
                transition: "all .3s ease",

                "&:hover": {
                    color: "white",
                    background: accentFillHover
                }
            }
        },

        // Compact floating button
        "& .cwt-compact-cta-button": {
            "font-weight": "600",
            padding: "10px 40px",
            "max-width": "unset",
            height: "auto",
            "& .cwt-icon": {
                "font-size": "18px",
                color: "white",

                "& .cwt-icon-label": {
                    "font-size": "16px",
                    padding: "0",
                    [applyLocalizedProperty("margin-right", "margin-left", config.direction)]: "10px",
                    color: "white"
                }
            }
        },

        // Overlay floating button
        "& button.cwt-fast-button.cwt-overlay-cta-button": {
            padding: "12px 15px",
            height: "auto",
            "box-shadow": "0px 1.2px 3.6px rgba(0, 0, 0, 0.1), 0px 6.4px 14.4px rgba(0, 0, 0, 0.13)",
            position: "fixed",
            [applyLocalizedProperty("right", "left", config.direction)]: "25px",
            bottom: "45px",
            "border-radius": "71px",
            "&:hover, &:focus": {
                "box-shadow": "0px 1.2px 3.6px rgba(0, 0, 0, 0.05), 0px 2.4px 6.4px rgba(0, 0, 0, 0.13)"
            },
            "& .cwt-icon": {
                "font-size": "20px",
                "font-weight": "bold",
                "align-items": "flex-end",
                color: accentForegroundCut,
                "& .cwt-icon-label": {
                    color: accentForegroundCut,
                    "line-height": "24px"
                }
            },
            "&.cwt-overlay-cta-button-desktop": {
                height: "69px",
                padding: "0 28px",
                "& .cwt-icon": {
                    [applyLocalizedProperty("margin-left", "margin-right", config.direction)]: "15px",
                    "font-weight": "normal",
                    "& .cwt-icon-label": {
                        display: "block",
                        [applyLocalizedProperty("margin-right", "margin-left", config.direction)]: "-8px"
                    }
                }
            }
        },

        [`&.${enums.responsiveLevels.L0}`]: {
            "max-width": "100vw"
        },

        [`&.${enums.responsiveLevels.MobilePopup}`]: {
            "scroll-margin-top": "6rem",
            "margin-left": "10px",
            "margin-right": "10px",
            "& button.cwt-fast-button.cwt-overlay-cta-button": {
                [applyLocalizedProperty("left", "right", config.direction)]: "50%",
                [applyLocalizedProperty("right", "left", config.direction)]: "unset",
                transform: applyLocalizedProperty("translateX(-50%)", "translateX(50%)", config.direction),
                "& .cwt-icon": {
                    "& .cwt-icon-label": {
                        [applyLocalizedProperty("margin-left", "margin-right", config.direction)]: "5px"
                    }
                }
            }
        }
    },
    commentMinimalContainer: {
        overflow: "hidden",
        position: "relative",
        margin: "32px 5px",
        padding: "0 15px 0",
        "box-shadow": "0px 0.6px 1.8px rgba(0, 0, 0, 0.1), 0px 3.2px 7.2px rgba(0, 0, 0, 0.13)",
        background: isDarkMode(config) ? "rgb(66, 66, 66)" : "white",
        border: "none",
        "& .cwt-content-disabled-container": {
            padding: "20px"
        },
        "& ul, li": {
            margin: "0px"
        },
        "& .cwt-join-container": {
            margin: "0 -15px",

            "& button.cwt-fast-button.cwt-compact-cta-button": {
                width: "100%",
                "padding-left": "0",
                "padding-right": "0"
            }
        },
        "& .cwt-message": {
            "max-height": "44px",
            overflow: "hidden"
        },
        "&:before": {
            content: "''",
            transition: "background-position 0.2s ease-in, opacity 0.1s linear 0.1s",
            width: "100%",
            height: "60%",
            position: "absolute",
            background: isDarkMode(config) ?  "linear-gradient(180deg, rgba(66, 66, 66, 0), rgba(66, 66, 66, 1))" : "linear-gradient(180deg, rgba(255, 255, 255, 0), #FFFFFF)",
            "background-repeat": "no-repeat",
            opacity: "100%",
            "z-index": "1",
            left: "0",
            right: "0",
            bottom: "44px"
        },
        "&:hover:before": {
            cursor: "pointer",
            "background-position": "0px 100px",
            opacity: "0%"
        },

        [`&.${enums.responsiveLevels.MobilePopup}`]: {
            "margin-left": "0",
            "margin-right": "0",
            "border-radius": "0",
            "box-shadow": "none"
        }
    },
    commentZeroStateContainer: {
        padding: "24px",
        border: "1px solid",
        "border-color": neutralFillStealthHover,
        background: isDarkMode(config) ? "rgb(66, 66, 66)" : "white",
        display: "flex",
        "flex-direction": "row",
        "justify-content": "space-between",
        "align-items": "center",
        "border-radius": "8px",
        [`&.${enums.responsiveLevels.MobilePopup}`]: {
            "padding-left": "0",
            "padding-right": "0",
            "margin-left": "0",
            "margin-right": "0",
            "border-top": "1px solid",
            "border-bottom": "1px solid",
            "border-color": neutralFillActive,
            "border-radius": "0",
            "flex-direction": "column",
            "justify-content": "start",
            "align-items": "center",
            "box-shadow": "none",
            "& button.cwt-start-button": {
                "margin-top": "5px"
            }
        }
    },
    commentOverlayContainer: {
        position: "fixed",
        "z-index": overlayZIndex,
        background: neutralLayerCard,
        border: "none",
        top: "0",
        margin: "0 auto",
        [applyLocalizedProperty("left", "right", config.direction)]: "100vw",
        width: "550px",
        height: "100vh",
        overflow: "auto",
        transition: ".2s ease-in",
        "& .cwt-close-button": {
            "z-index": "1",
            position: "fixed",
            [applyLocalizedProperty("right", "left", config.direction)]: "32px",
            top: "18px",
            "& .cwt-icon": {
                color: isDarkMode(config) ? "white" :"black",
                "font-size": "16px"
            }
        },
        "& .cwt-avatar-container": {
            height: "32px",
            [applyLocalizedProperty("margin-right", "margin-left", config.direction)]: "10px",
            [applyLocalizedProperty("margin-left", "margin-right", config.direction)]: "-13px",
            "& .cwt-avatar": {
                "min-width": "32px",
                "max-width": "32px",
                height: "32px",
                "border-radius": "50%",
                border: "1px solid white"
            }
        },
        [`&.${enums.responsiveLevels.MobilePopup}`]: {
            margin: "0",
            width: "100vw",
            "& .overlay-title-container": {
                position: "sticky",
                "z-index": "3",
                top: `-${mainPadding}px`,
                display: "flex",
                "align-items": "center",
                width: "calc(100vw - 38px)",
                "margin-top": `-${mainPadding}px`,
                "margin-bottom": "30px",
                [applyLocalizedProperty("margin-left", "margin-right", config.direction)]: `-${mainPadding}px`,
                padding: "20px 8px 16px",
                [applyLocalizedProperty("padding-right", "padding-left", config.direction)]: "30px",
                background: neutralFillStealthActive,
                "border-bottom": "1px solid",
                "border-color": neutralFillActive,
                "& .cwt-image-container": {
                    "min-width": "40px",
                    width: "40px",
                    height: "40px",
                    [applyLocalizedProperty("margin-right", "margin-left", config.direction)]: "12px",
                    [applyLocalizedProperty("margin-left", "margin-right", config.direction)]: "16px",
                    "& .cwt-content-image": {
                        "object-fit": "cover",
                        "border-radius": "2px"
                    }
                },
                "& button.cwt-fast-button.cwt-navbar-button": {
                    padding: "4px",
                    "& .cwt-icon": {
                        color: neutralForegroundRest,
                        "font-size": "16px"
                    }
                },
                "& .cwt-title": {
                    display: "-webkit-box",
                    "-webkit-line-clamp": "2",
                    "-webkit-box-orient": "vertical",
                    overflow: "hidden",
                    "text-overflow": "ellipsis",
                    "font-weight": "600",
                    "line-height": "24px",
                    color: neutralForegroundRest
                }
            }
        }
    },

    commentOverlayActiveContainer: {
        padding: "12px 32px",
        "box-shadow": "0px 9px 20px rgba(0, 0, 0, 0.12)",
        [applyLocalizedProperty("left", "right", config.direction)]: "calc(100% - 550px)",
        [`&.${enums.responsiveLevels.MobilePopup}`]: {
            padding: "15px 15px 45px",
            [applyLocalizedProperty("left", "right", config.direction)]: "0"
        },
        "& $commentItemContainer": {
            "&.cwt-specific-comment": {
                [applyLocalizedProperty("margin-left", "margin-right", config.direction)]: "-32px !important",
                "padding-left": "32px !important",
                "padding-right": "32px !important",
                "&.cwt-specific-reply": {
                    [applyLocalizedProperty("margin-left", "margin-right", config.direction)]: `calc(0px - (${avatarPadding} + 32px)) !important`,
                    [applyLocalizedProperty("padding-left", "padding-right", config.direction)]: `calc(${avatarPadding} + 32px) !important`
                }
            }
        }
    },

    buttonFadeInContainer: {
        animation: "button-fade-in .4s ease",
        "-webkit-animation-fill-mode": "forwards"
    },

    buttonFadeOutContainer: {
        animation: "button-fade-out .4s ease",
        "-webkit-animation-fill-mode": "forwards"
    },

    "@keyframes button-fade-in": {
        from: { bottom: "-45px"},
        "90%": {bottom: "50px"},
        to: { bottom: "45px"}
    },

    "@keyframes button-fade-out": {
        from: { bottom: "45px", opacity: "1"},
        to: { bottom: "-45px", opacity: "0"}
    }
});